// src/pages/CurriculumVitae.jsx
import React, { useRef } from 'react';
import avatarIcon from '../assets/images/bmp314357.svg';
import ContextNoteDialog from '../components/dialogs/ContextNoteDialog';

const tradingSystemData = {
  title:    "Crypto Trading Automation System",
  subtitle: "Beyond just programming — a full operational build",
  lamp:     "#10b981",
  brand:    "Vision Tools · Trading System",
  storageKey: null,
  tags: [
    { label: "Featured",      color: "orange" },
    { label: "Node.js",       color: "green"  },
    { label: "Google Vision", color: "blue"   },
    { label: "Solana",        color: "purple" },
  ],
  sections: [
    {
      label: "More than programming",
      body: `This wasn't just a coding project — it was a full operational system.
             A <strong style="color:rgba(255,255,255,0.88)">12-screen workstation</strong> was built and tuned
             for 24/7 rhythm. Discord received injected CSS scripts to enable auto-scroll and
             OCR-optimized fonts and backgrounds for reliable capture.
             A minimalistic Sheets layer tracked per-token and per-caller performance metrics.
             <br/><br/>
             Patch scripts were written after heuristic discoveries — for example, Google Vision OCR
             would consistently mis-case letters surrounded by two opposite-case neighbors.
             Contract address validation was solved by generating single-character reversal variants,
             firing all requests in parallel to Alchemy, and returning the correct address if any matched —
             <strong style="color:rgba(255,255,255,0.88)">dramatically cutting failed trade reads</strong>.`,
    },
    {
      label: "Scope",
      body: `Monitored <strong style="color:rgba(255,255,255,0.88)">12 Discord / Telegram channels</strong> simultaneously.
             Executed <strong style="color:rgba(255,255,255,0.88)">1,000+ trades</strong> and analyzed
             <strong style="color:rgba(255,255,255,0.88)">15,000+ calls</strong> over a 2-month period (Jan–Mar 2025).`,
    },
    {
      label: "Available on request",
      body: `Two trading wallets, collected dataset, and evaluation models can be provided on request at some reasonable context.
             <br/><span style="color:rgba(255,255,255,0.42)">Not published here intentionally.</span>`,
    },
  ],
};

const CV = () => {
  const tradingNoteRef = useRef();

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-start justify-start text-white p-4 md:p-12 overflow-y-auto">
      <div className="space-y-4 max-w-6xl w-full">

        {/* Name with Avatar */}
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <img
              src={avatarIcon}
              alt="Avatar"
              className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-purple-500"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Ante Lokas</h1>
            <h2 className="text-lg text-gray-300">Web Developer | Data & AI</h2>
            <p className="text-gray-200">📍 Location: Croatia</p>
            {/* GitHub link */}
            <a
            href="https://github.com/M-Advis"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-1 text-sm text-gray-400 hover:text-white transition-colors duration-150"
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
            </svg>
            github.com/M-Advis
            </a>
          </div>
        </div>

        {/* ── Left (Education + Experience) | Right (Recent Activity) ── */}
        <div className="pt-4 flex flex-col md:flex-row items-start" style={{ gap: 0 }}>

          {/* ── LEFT: Education + Work Experience ── */}
          <div className="flex-1 space-y-6 pr-0 md:pr-8">

            {/* Education */}
            <div>
              <h3 className="font-semibold underline text-white flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></span>
                Education
              </h3>
              <p className="text-gray-200">Bachelor of Science in Computer Science</p>
              <p className="text-sm text-gray-400">Prirodoslovno-matematički fakultet, Split, Croatia (2013 – 2018)</p>
            </div>

            {/* Work Experience */}
            <div>
              <h3 className="font-semibold underline text-white flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></span>
                Work Experience
              </h3>

              {/* goigo.agency */}
              <div className="mt-2 pt-3 border-t border-gray-700">
                <p className="text-gray-200">--- Web Developer</p>
                <p className="text-sm text-gray-400">
                  <a
                    href="https://goigo.agency"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    goigo.agency
                  </a>
                  {" "}| Apr 2024 – Present
                </p>
                <p className="text-sm text-gray-400 max-w-2xl">
                  Frontend development & design for custom yacht charter booking websites.
                  Building clean & optimized Bootstrap layouts with raw JS/CSS micro components — including
                  draggable UI elements and booking calendars. Customizing and extending third-party libraries
                  (Litepicker, Chosen) to fit specific project requirements.
                  Establishing a general template base gradually shifting toward fully custom-built components.
                </p>
              </div>

              {/* Freelance */}
              <div className="mt-3">
                <p className="text-gray-200">-- Freelance Projects</p>
                <p className="text-sm text-gray-400">Mar 2020 – Present</p>
                <p className="text-sm text-gray-400 max-w-2xl">
                  Worked as a contractor on various projects throughout the years. Developed and deployed
                  full-stack web applications primarily using React for frontend and Node.js for backend,
                  tailored specifically for client needs across various industries.
                </p>
              </div>

              {/* Fish Farm */}
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-gray-200">- Fish Farm managment</p>
                <p className="text-sm text-gray-400">| Oct 2018 – Mar 2024</p>
                <p className="text-sm text-gray-400 max-w-2xl">
                  Managed fish farm with 10+ employees and 100+ tons of fish under management.
                  Handled logistics, quality control, client relations, and operational planning.
                  This commitment is primarily why my development portfolio appears limited during this period—
                  balancing both left limited time for IT projects.
                </p>
              </div>
            </div>

          </div>

          {/* ── Vertical separator ── */}
          <div
            className="hidden md:block"
            style={{
              width: 1,
              alignSelf: "stretch",
              background: "rgba(255,255,255,0.08)",
              margin: "0 32px",
              flexShrink: 0,
            }}
          />

          {/* ── RIGHT: Recent Activity ── */}
          
          
          <div className="flex-1 pl-0 md:pl-0 mt-6 md:mt-0">
            <h3 className="font-semibold underline text-white flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></span>
              Recent Additional Activity
            </h3>
            
            <div className="border border-gray-700 rounded-lg p-4 mt-4">
            <h4 className="text-base font-semibold text-green-400 mb-2">Open-source contribution</h4>
              <p className="text-gray-200 font-medium flex items-center flex-wrap gap-2 mb-2">
                <span className="text-xl">📉</span>
                Nutilis Trader
                <a
                  onClick={() => window.open("https://github.com/M-Advis", "_blank")}
                  className="inline-flex items-center gap-1 text-sm font-medium text-gray-400 border border-gray-600/50 rounded-lg px-3 py-1 hover:bg-gray-400/10 hover:border-gray-400/70 hover:text-gray-300 active:scale-95 transition-all duration-150 cursor-pointer"
                >
                  GitHub ↗
                </a>
              </p>
              <p className="text-sm text-gray-400 italic mb-2">
                15th April - Ongoing &nbsp;·&nbsp;
              </p>
              <p className="text-sm text-gray-400 mb-2">
                Nutilis Trader — a open-source project I find
                interesting, not just technically but as a system to understand deeply. Started with small contributions, might build up some indicators with it at some point. Project I'd genuinely like to work for.
 
              </p>
              <p className="text-xs text-gray-500">
                Python • Data Analysis • Strategy Research • GitHub
              </p>
            </div>
            <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="text-base font-semibold text-blue-400 mb-2">Data Science & Machine Learning</h4>
              <p className="text-gray-200 font-medium flex items-center flex-wrap gap-2 mb-2">
                <span className="text-xl">📈</span>
                Telecom Customer Churn Prediction
                <a
                  href="https://www.kaggle.com/code/antelokas/telecom-churn-first-competition"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-400 border border-blue-400/50 rounded-lg px-3 py-1 hover:bg-blue-400/15 hover:border-blue-400/80 hover:text-blue-300 active:scale-95 transition-all duration-150"
                >
                  Kaggle ↗
                </a>
              </p>
              <p className="text-sm text-gray-400 italic mb-2">
                ML - Data Science project &nbsp;·&nbsp; Last updated: Mar 16, 2026
              </p>
              <p className="text-sm text-gray-400 mb-2">
                Building a churn prediction pipeline as part of active data science training on Kaggle.
                Implemented custom feature engineering including billing gap analysis and stickiness scoring with
                a LightGBM model achieving a strong baseline AUC. Focused on modular, reproducible workflow design.
                Culminating in a comprehensive Jupyter Notebook documenting all approaches, experiments, and
                ongoing research findings in one place.
              </p>
              <p className="text-xs text-gray-500">
                Python • LightGBM • Pandas • Scikit-learn • Feature Engineering • Kaggle
              </p>
            </div>
          </div>

        </div>

        {/* Projects */}
        <div className="pt-4 pb-8">
          <h3 className="font-semibold underline text-white flex items-center gap-2 mb-4">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></span>
            Projects highlight
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Left Column - Full Stack */}
            <div className="space-y-6">
              <h4 className="text-lg font-semibold text-purple-400 mb-4">Full Stack Web Applications</h4>
              {/* Boat Rental */}
              <div className="border border-gray-700 rounded-lg p-4">
                <div className="bg-gray-800 rounded mb-3 overflow-hidden">
                  <iframe
                    width="100%"
                    height="200"
                    src="https://www.youtube.com/embed/LjhksnqibAk"
                    title="Boat Rental Platform"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  ></iframe>
                </div>
                <p className="text-gray-200 font-medium mb-2 flex items-center gap-2">
                  <span>⛵</span>
                  Boat Rental Platform
                </p>
                <p className="text-sm text-gray-400 mb-2">
                  B2B platform connecting tour providers with booking agencies. Fully responsive application allowing
                  tour providers to showcase available spots while agencies can book directly for end-user requests.
                  Features real-time availability tracking and streamlined booking workflow.
                </p>
                <p className="text-xs text-gray-500">React • Tailwind CSS • Material-UI • Node.js • Strapi • MySQL</p>
              </div>

              {/* Discount Coupons */}
              <div className="border border-gray-700 rounded-lg p-4">
                <p className="text-gray-200 font-medium mb-2 flex items-center gap-2">
                  <span>🎟️</span>
                  Discount Coupon Management System
                </p>
                <p className="text-sm text-gray-400 mb-2">
                  Full-stack coupon code management platform enabling businesses to create, distribute, and track
                  promotional discount codes.
                </p>
                <p className="text-xs text-gray-500">React • Tailwind CSS • Material-UI • Node.js • Strapi • MySQL</p>
              </div>
            </div>

            {/* Right Column - Crypto */}
            <div className="space-y-6">
              <h4 className="text-lg font-semibold text-green-400 mb-4">Crypto & Trading Projects</h4>

              {/* Crypto Trading Bot */}
              <div className="border border-gray-700 rounded-lg p-4">
                <p className="text-gray-200 font-medium flex items-center gap-2 mb-2">
                  <span className="text-xl">🤖</span>
                  Crypto Trading Automation System
                </p>
                <p className="text-sm text-gray-400 italic mb-3">Event-driven trading bot with multi-channel monitoring</p>
                <p className="text-sm text-gray-400 mb-3">
                  Built an automated trading system monitoring 12 Discord/Telegram channels simultaneously.
                  Utilized Google Cloud Vision AI for OCR-based token address detection from screenshots,
                  triggering instant trades via Telegram bot integration when specific members mentioned tokens.
                </p>
                <p className="text-sm text-gray-400 mb-3">
                  Implemented comprehensive analytics dashboard tracking profitability
                  per caller and per channel. Executed 1,000+ trades and analyzed 15,000+ calls over
                  2-month period (Jan-Mar 2025).
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Node.js • Google Cloud Vision API • Telegram Bot API • Event-driven Architecture • Remote Desktop Automation
                </p>

                {/* Trophy button */}
                <div
                  onClick={() => tradingNoteRef.current?.handleOpen()}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    cursor: "pointer",
                    padding: "5px 14px",
                    border: "1px solid rgba(249,115,22,0.35)",
                    background: "rgba(249,115,22,0.08)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "rgba(249,115,22,0.15)";
                    e.currentTarget.style.borderColor = "rgba(249,115,22,0.6)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "rgba(249,115,22,0.08)";
                    e.currentTarget.style.borderColor = "rgba(249,115,22,0.35)";
                  }}
                >
                  <span style={{ fontSize: 14 }}>🏆</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
                    textTransform: "uppercase", fontFamily: "'Barlow', sans-serif",
                    color: "#fb923c",
                  }}>
                    Featured — Full breakdown
                  </span>
                </div>
              </div>

              {/* Token Holders Analytics */}
              <div className="border border-gray-700 rounded-lg p-4">
                <div className="bg-gray-800 rounded mb-3 overflow-hidden">
                  <iframe
                    width="100%"
                    height="200"
                    src="https://www.youtube.com/embed/22hdkFlM0xE"
                    title="Token Holders Analytics"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  ></iframe>
                </div>
                <p className="text-gray-200 font-medium mb-2 flex items-center flex-wrap gap-2">
                  <span>📊</span>
                  Token Holders Analytics Dashboard
                  <a
                    href="/Structure info"
                    className="inline-flex items-center gap-1 text-sm font-medium text-green-400 border border-green-400/50 rounded-lg px-3 py-1 hover:bg-green-400/15 hover:border-green-400/80 hover:text-green-300 active:scale-95 transition-all duration-150"
                  >
                    Live demo ↗
                  </a>
                </p>
                <p className="text-sm text-gray-400 mb-2">
                  Analytics metric for cryptocurrency token holders. Breaks down blockchain
                  data into creative visualizations of holder distribution analysis and profit/loss tracking.
                  Real-time data processing with intuitive charts for actionable trading insights.
                </p>
                <p className="text-xs text-gray-500">
                  React • Recharts • Material-UI • GraphQL • Bitquery API • Data Visualization
                </p>
              </div>
            </div>

            {/* Terminal Thank You */}
            <div className="col-span-1 md:col-span-2 mt-12 mb-8 mx-auto max-w-2xl border-2 border-green-500/30 rounded-lg p-6 bg-transparent">
              <pre className="text-green-400 text-xs md:text-sm font-mono whitespace-pre">
{`┌─────────────────────────────────────────┐
│                                         │
│  $ echo "Thank you for your time"       │
│                                         │
│  > Reviewing CV...                      │
│  > Processing portfolio...              │
│  > Appreciation acknowledged            │
│                                         │
│  Feel free to reach out:                │
│  > Email: inverted2logic@gmail.com      │
│  > Telegram: @RickFlick505              │
│                                         │
└─────────────────────────────────────────┘`}
              </pre>
            </div>

          </div>
        </div>

      </div>

      {/* Dialog — mounted at root level so fixed positioning works correctly */}
      <ContextNoteDialog ref={tradingNoteRef} data={tradingSystemData} />

    </div>
  );
};

export default CV;